import { Text } from 'src/design/atoms/Text';
import { MRT_ColumnDef } from 'mantine-react-table';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { JobDto } from 'src/api/FiretrolApiClient';
import { ViewActionIcon } from 'src/design/atoms/ViewActionIcon';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';

interface JobTableProps {
  jobs: JobDto[];
  loading: boolean;
}

export const JobTable: React.FC<JobTableProps> = ({ jobs, loading }) => {
  const navigate = useNavigate();

  const handleView = (row: JobDto) => {
    navigate(`/jobs/${row.id}/summary`);
  };

  const columns = React.useMemo<MRT_ColumnDef<JobDto>[]>(
    () => [
      {
        id: 'job',
        accessorFn: (row) => `${row.jobNumber} - ${row.name}`,
        header: 'Job',
        size: 325,
      },
      {
        id: 'division',
        accessorFn: (row) => row.divisionEntity?.name || row.division || '--',
        header: 'Division',
        size: 150,
      },
      {
        id: 'milestone',
        accessorFn: (row) => row.milestone?.title || '--',
        header: 'Milestone',
        size: 150,
      },
      {
        id: 'customerName',
        accessorFn: (row) => row.customerName || '--',
        header: 'Customer',
        size: 200,
      },
      {
        id: 'description',
        accessorFn: (row) =>
          row.description && row.description.length > 0
            ? row.description
            : '--',
        header: 'Description',
        size: 300,
        Cell: ({ cell }) => (
          <Text
            c="black"
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {cell.getValue<string>()}
          </Text>
        ),
      },
      {
        id: 'location',
        accessorFn: (row) => row.location?.address || '--',
        header: 'Jobsite Location',
        size: 250,
      },
    ],
    [],
  );

  const tableState = useTableState({
    identifier: 'jobs-table',
    columns: columns,
    defaultSorting: [{ id: 'job', desc: false }],
  });

  const tableExports = useTableExports<JobDto>('jobs', (data) => {
    return data.map((item) => ({
      ['Job Number']: item.jobNumber,
      ['Job Name']: item.name,
      ['Division']: item.divisionEntity?.name || item.division || '',
      ['Milestone']: item.milestone?.title || '',
      ['Customer']: item.customerName || '',
      ['Description']: item.description || '',
      ['Jobsite Location']: item.location?.address || '',
    }));
  });

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 100 } },
      renderRowActions: ({ row }) => (
        <ViewActionIcon onClick={() => handleView(row.original)} />
      ),
      enableClickToCopy: false,
      columns: columns,
      data: jobs,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
    },
    getRowId: (row) => String(row.jobNumber),
    totalCount: jobs.length,
    isLoading: loading,
    isFetching: false,
    isError: false,
    ...tableExports,
  });

  return table;
};
